import { defineStore } from "pinia";
import api from "@/modules/api";
import { Notification } from "@/types/entity";
import { useMate } from "./mate";
import { computed, ref, watch } from "vue";
import { cursorPagerStoreFactory } from "@/stores/factory/list-store-factory";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { getDisplayDateTime } from "@/modules/common";
import notificationUtils from "@/stores/notification-utils";
import { NotifyData } from "@/types/notification";
import { NotificationSubType } from "@/enums/notificationEnum";
import { useMateCommit } from "@/stores/mateCommit";
import { useScopedMateId } from "@/composable/useScopedMateId";
import {
  ApiGetInteractionPoolSummary,
  InteractionPoolSummary,
} from "@/apis/interaction";
import { useGlobalUser } from "./globalUser";

const PAGE_SIZE = 12;

const cursorEncoder = (
  entity: Notification<any>,
  pointsToNextItems: boolean
) => {
  return btoa(
    JSON.stringify({
      id: entity.id,
      updated_at: getDisplayDateTime(entity.dtUpdate, true, true),
      _pointsToNextItems: pointsToNextItems,
    })
  );
};
export const useNotification = defineStore("notification", () => {
  const readTypeFilter = ref<string[]>([]);
  const unreadTypeFilter = ref<string[]>([]);

  const readSubTypeFilter = computed(() => {
    return readTypeFilter.value.length > 0
      ? NotificationSubType[readTypeFilter.value[0]].types
      : undefined;
  });
  const unreadSubTypeFilter = computed(() => {
    return unreadTypeFilter.value.length > 0
      ? NotificationSubType[unreadTypeFilter.value[0]].types
      : undefined;
  });

  const entityState = entityStoreFactory<Notification<NotifyData>, "id">("id");
  const { updateMap, getEntity } = entityState;
  const {
    state: read,
    dataList: readDataList,
    fetchNewerList: fetchNewerReadList,
    fetchOlderList: fetchOlderReadList,
    initListIfAbsent: initReadListIfAbsent,
    resetState: resetReadData,
  } = cursorPagerStoreFactory({
    entityStore: entityState,
    fetch: async (cursor?: string) => {
      const res = await api.readNotificationPage({
        filter: {
          type: "read",
          subType: readSubTypeFilter.value,
        },
        role: "mates",
        pagination: { perPage: PAGE_SIZE },
        sort: { field: "updated_at", order: "desc" },
        cursor,
      });
      updateMap(res.page.data);
      return res.page.data;
    },
    cursorEncoder,
  });
  const {
    state: unread,
    dataList: unreadDataList,
    fetchNewerList: fetchNewerUnreadList,
    fetchOlderList: fetchOlderUnreadList,
    initListIfAbsent: initUnreadListIfAbsent,
    resetState: resetUnreadData,
  } = cursorPagerStoreFactory({
    entityStore: entityState,
    fetch: async (cursor?: string) => {
      const res = await api.readNotificationPage({
        filter: {
          type: "unread",
          subType: unreadSubTypeFilter.value,
        },
        role: "mates",
        pagination: { perPage: PAGE_SIZE },
        sort: { field: "updated_at", order: "desc" },
        cursor,
      });
      updateMap(res.page.data);
      return res.page.data;
    },
    cursorEncoder,
  });
  // State
  const mateId2UnreadCount = ref<Record<number, number>>({});
  // Computed
  const unreadList = computed(() =>
    unreadDataList.value.map(notificationUtils.standardNotificationItemFactory)
  );
  const readList = computed(() =>
    readDataList.value.map(notificationUtils.standardNotificationItemFactory)
  );
  const mateStore = useMate();
  const totalUnreadNotificationCount = computed(() =>
    Math.min(
      Object.values(mateId2UnreadCount.value).reduce(
        (acc, count) => acc + count,
        0
      ),
      99
    )
  );
  const currentMateUnreadCount = computed(() =>
    mateStore.currentMate
      ? mateId2UnreadCount.value[mateStore.currentMate.id!]
      : 0
  );
  const commit = useMateCommit();
  const displayBadgeCount = computed(() => {
    const user = useGlobalUser();
    return (
      commit.pendingCount +
      totalUnreadNotificationCount.value +
      user.rewardCount
    );
  });
  // watch
  watch(
    () => mateStore.fullList,
    (list) => {
      if (list.length === 0) {
        return;
      }
      mateId2UnreadCount.value = list.reduce((acc, mate) => {
        acc[mate.id] = mate.unreadNotificationsCount;
        return acc;
      }, {} as Record<number, number>);
    },
    { immediate: true }
  );

  // Actions
  function getUnreadCountByMateId(mateId: number) {
    return mateId2UnreadCount.value[mateId];
  }

  async function readNotification(id: string): Promise<void> {
    const item = unread.value.list.find((item) => item === id);
    unread.value.list = unread.value.list.filter((item) => item !== id);
    if (item) {
      read.value.list.unshift(item);
    }
    const userReadNotification = useScopedMateId(async () => {
      await api.changeNotificationRead(id);
      await refreshUnreadCount();
    });
    await userReadNotification();
  }

  async function readAllNotification(): Promise<void> {
    await api.changeNotificationReadAll();
    mateId2UnreadCount.value = {};
    resetReadData();
    resetUnreadData();
  }

  async function refreshUnreadCount(): Promise<void> {
    const res = await api.readNotificationSummary();
    if (mateStore.userMate) {
      mateId2UnreadCount.value = {
        [mateStore.userMate.id!]: res.data.unreadNum,
      };
    }
    initSummaryCount();
  }

  function disableCache(): void {
    resetReadData();
    resetUnreadData();
  }

  const interactionSummary = ref<InteractionPoolSummary>();

  const displayInteractionPoolSummary = computed(() => {
    if (!interactionSummary.value) return 0;
    return (
      // interactionSummary.value.pendingFriendRequestCount +
      interactionSummary.value.pendingInteractionReviewCount +
      interactionSummary.value.unreadMailCount +
      interactionSummary.value.awaitingReceptionDispatchCount
    );
  });

  const initSummaryCount = () => {
    ApiGetInteractionPoolSummary().then((res) => {
      interactionSummary.value = res.data;
    });
  };

  return {
    // State
    unread,
    read,
    unreadList,
    readList,
    mateId2UnreadCount,
    totalUnreadNotificationCount,
    currentMateUnreadCount,
    readTypeFilter,
    unreadTypeFilter,
    displayBadgeCount,

    interactionSummary,
    displayInteractionPoolSummary,
    // Actions
    getUnreadCountByMateId,
    initReadListIfAbsent,
    initUnreadListIfAbsent,
    fetchNewerReadList,
    fetchOlderReadList,
    fetchNewerUnreadList,
    fetchOlderUnreadList,
    readNotification,
    readAllNotification,
    refreshUnreadCount,
    resetReadData,
    resetUnreadData,
    disableCache,
    initSummaryCount,
  };
});
